import { ReactNode, useMemo } from 'react';
import { AuthContext } from 'react-oidc-context';
import type { AuthContextProps } from 'react-oidc-context';

// @ohpen/react-auth's useAuth (dist/index.mjs:466-475) extracts userName by
// base64-decoding the JWT payload. Mint a fake JWT so userName resolves to
// "harness-user" — matching the BFF harness principal's Name claim.
const mintHarnessJwt = () => {
  const payload = btoa(JSON.stringify({ username: 'harness-user' }))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  return `eyJhbGciOiJub25lIn0.${payload}.sig`;
};

// Groups drive the role labels shown in the user menu (AppLayout). Authorization
// itself now comes from GET /me/permissions, resolved by the BFF harness principal.
const HARNESS_GROUPS = '["PMP_Update","PMP_Reviewers","PMP_Lenders"]';

const harnessOidcValue = {
  isAuthenticated: true,
  isLoading: false,
  activeNavigator: undefined,
  error: undefined,
  user: {
    access_token: mintHarnessJwt(),
    token_type: 'Bearer',
    scope: 'openid profile email',
    profile: {
      email: 'harness-user@harness.test',
      name: 'Harness User',
      nickname: 'harness-user',
      sub: '00000000-0000-0000-0000-000000000001',
      'custom:displayname': 'Harness User',
      'custom:groups': HARNESS_GROUPS,
    },
    session_state: null,
    expires_at: 9999999999,
  },
  settings: {
    client_id: 'harness',
    post_logout_redirect_uri: '/',
  },
  events: { addUserLoaded: () => () => {}, removeUserLoaded: () => {} } as unknown,
  signinRedirect: async () => {},
  signoutRedirect: async () => {},
  signinSilent: async () => null,
  signinPopup: async () => null,
  signinResourceOwnerCredentials: async () => null,
  signoutSilent: async () => {},
  signoutPopup: async () => {},
  removeUser: async () => {},
  querySessionStatus: async () => null,
  startSilentRenew: () => {},
  stopSilentRenew: () => {},
  clearStaleState: async () => {},
  revokeTokens: async () => {},
} as unknown as AuthContextProps;

export function HarnessAuthProvider({ children }: { readonly children: ReactNode }) {
  const value = useMemo(() => harnessOidcValue, []);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
